import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { purchaseShippingLabel, voidShippingLabel } from "@/lib/shipping-labels.functions";
import { allocateSscc } from "@/lib/admin.functions";
import { evaluateBolRequirement, listBolsForShipment } from "@/lib/bol.functions";
import { validateShipmentCompliance, resendShipmentAsn } from "@/lib/retail-compliance.functions";
import {
  getShipment,
  getShipmentLpns,
  getShipmentEvents,
  createShipmentLpn,
} from "@/lib/operations.functions";
import {
  getShipmentOperationalSummary,
} from "@/lib/shipments/summary.functions";
import { ShipmentSummaryHeader } from "@/components/shipments/shipment-summary-header";
import { runServerFn } from "@/lib/server-fn";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { StatusPill, statusTone } from "@/components/ui/status-pill";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Barcode } from "@/components/barcode";
import { Plus, Loader2, FileText, Printer, Layers, Truck, ExternalLink, X } from "lucide-react";
import { AlertTriangle } from "lucide-react";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated/app/shipments/$shipmentId/")({ component: ShipmentDetailPage });

function ComplianceArtifactRow(props: { label: string; status: string; detail: string }) {
  const tone =
    props.status === "present" ? "secondary"
      : props.status === "not_required" ? "outline"
      : "destructive";
  return (
    <div className="rounded-md border p-2 space-y-1">
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium">{props.label}</span>
        <Badge variant={tone as any} className="text-[10px]">{props.status.replace("_", " ")}</Badge>
      </div>
      <div className="text-xs text-muted-foreground">{props.detail}</div>
    </div>
  );
}

function ShipmentDetailPage() {
  const { shipmentId } = Route.useParams();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const fetchShipment = useServerFn(getShipment);
  const fetchLpns = useServerFn(getShipmentLpns);
  const fetchEvents = useServerFn(getShipmentEvents);
  const createLpnFn = useServerFn(createShipmentLpn);
  const fetchSummary = useServerFn(getShipmentOperationalSummary);

  const { data: shp } = useQuery({
    queryKey: ["shipment", shipmentId],
    queryFn: () => fetchShipment({ data: { shipmentId } }),
  });

  const { data: summary } = useQuery({
    queryKey: ["shipment-summary", shipmentId],
    queryFn: () => fetchSummary({ data: { shipmentId } }),
  });

  const { data: lpns = [], refetch: refetchLpns } = useQuery({
    queryKey: ["lpns", shipmentId],
    queryFn: () => fetchLpns({ data: { shipmentId } }),
  });

  const { data: events = [] } = useQuery({
    queryKey: ["shipment-events", shipmentId],
    queryFn: () => fetchEvents({ data: { shipmentId } }),
  });

  const evalFn = useServerFn(evaluateBolRequirement);
  const bolListFn = useServerFn(listBolsForShipment);
  const { data: bolEval } = useQuery({
    queryKey: ["bol-eval", shipmentId],
    queryFn: () => evalFn({ data: { shipmentId } }),
  });
  const { data: bolList } = useQuery({
    queryKey: ["bol-list", shipmentId],
    queryFn: () => bolListFn({ data: { shipmentId } }),
  });
  const activeBol = (bolList?.bols ?? []).find((b: any) => b.status !== "voided");

  const complianceFn = useServerFn(validateShipmentCompliance);
  const { data: compliance, refetch: refetchCompliance } = useQuery({
    queryKey: ["compliance", shipmentId],
    queryFn: () => complianceFn({ data: { shipmentId } }),
  });
  const resendFn = useServerFn(resendShipmentAsn);
  const [resendBusy, setResendBusy] = useState(false);
  const [resendReason, setResendReason] = useState("");
  const [resendOpen, setResendOpen] = useState(false);
  const onResend856 = async () => {
    if (!resendReason.trim() || resendReason.length < 2) {
      toast.error("Reason is required");
      return;
    }
    setResendBusy(true);
    try {
      const res = await resendFn({ data: { shipmentId, reason: resendReason.trim() } });
      toast.success(`856 resent (control # ${res.controlNumber})`);
      setResendOpen(false); setResendReason("");
      refetchCompliance();
    } catch (e: any) {
      toast.error(e?.message ?? "Resend failed");
    } finally {
      setResendBusy(false);
    }
  };

  const [open, setOpen] = useState(false);
  const [type, setType] = useState<"pallet" | "carton" | "tote">("pallet");
  const [weight, setWeight] = useState("");
  const [busy, setBusy] = useState(false);

  const buyLabel = useServerFn(purchaseShippingLabel);
  const voidLabel = useServerFn(voidShippingLabel);
  const allocateSsccFn = useServerFn(allocateSscc);
  const [labelOpen, setLabelOpen] = useState(false);
  const [labelCarrier, setLabelCarrier] = useState<"ups" | "usps">("ups");
  const [labelService, setLabelService] = useState("03");
  const [labelBusy, setLabelBusy] = useState(false);
  const [labelWeight, setLabelWeight] = useState("");
  const [labelLength, setLabelLength] = useState("");
  const [labelWidth, setLabelWidth] = useState("");
  const [labelHeight, setLabelHeight] = useState("");

  const services: Record<string, { value: string; label: string }[]> = {
    ups: [
      { value: "03", label: "UPS Ground" },
      { value: "02", label: "UPS 2nd Day Air" },
      { value: "01", label: "UPS Next Day Air" },
    ],
    usps: [
      { value: "PRIORITY", label: "USPS Priority Mail" },
      { value: "FIRST_CLASS", label: "USPS First Class" },
      { value: "GROUND_ADVANTAGE", label: "USPS Ground Advantage" },
    ],
  };

  // Prefill carrier/service + parcel dims/weight from packed carton whenever
  // the dialog opens. Operator can still override before purchasing.
  const openLabelDialog = () => {
    const pkg = shp?.shipment_packages?.[0];
    const initialCarrier = (shp?.carrier === "usps" ? "usps" : "ups") as "ups" | "usps";
    setLabelCarrier(initialCarrier);
    const matched = services[initialCarrier].find((s) => s.value === shp?.service_level);
    setLabelService(matched?.value ?? services[initialCarrier][0].value);
    setLabelWeight(pkg?.weight_grams ? (Number(pkg.weight_grams) / 28.3495).toFixed(2) : "");
    setLabelLength(pkg?.length_mm ? (Number(pkg.length_mm) / 25.4).toFixed(1) : "");
    setLabelWidth(pkg?.width_mm ? (Number(pkg.width_mm) / 25.4).toFixed(1) : "");
    setLabelHeight(pkg?.height_mm ? (Number(pkg.height_mm) / 25.4).toFixed(1) : "");
    setLabelOpen(true);
  };

  const onBuyLabel = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!labelWeight || Number(labelWeight) <= 0) {
      toast.error("Enter a carton weight before buying a label");
      return;
    }
    setLabelBusy(true);
    try {
      const res = await buyLabel({ data: {
        shipment_id: shipmentId,
        carrier: labelCarrier,
        service_code: labelService,
        accept_address_warnings: true,
        weight_oz: Number(labelWeight),
        length_in: labelLength ? Number(labelLength) : undefined,
        width_in: labelWidth ? Number(labelWidth) : undefined,
        height_in: labelHeight ? Number(labelHeight) : undefined,
      } });
      toast.success(`Label purchased · ${res.tracking}`);
      setLabelOpen(false);
      qc.invalidateQueries({ queryKey: ["shipment", shipmentId] });
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setLabelBusy(false);
    }
  };

  const onVoidLabel = async () => {
    if (!confirm("Void this label? Tracking will be cleared.")) return;
    try {
      await voidLabel({ data: { shipment_id: shipmentId } });
      toast.success("Label voided");
      qc.invalidateQueries({ queryKey: ["shipment", shipmentId] });
    } catch (err) {
      toast.error((err as Error).message);
    }
  };

  const onReprintLabel = () => {
    if (!shp?.label_url) return;
    const w = window.open(shp.label_url, "_blank", "noopener,noreferrer");
    if (!w) {
      toast.warning("Pop-up blocked — allow pop-ups to reprint the label");
      return;
    }
    // Most browsers will render the PDF; trigger the print dialog once loaded.
    try {
      w.addEventListener("load", () => {
        try {
          w.focus();
          w.print();
        } catch {
          /* user can still print manually */
        }
      });
    } catch {
      /* cross-origin storage URL; user prints from the PDF viewer */
    }
    toast.success("Label opened — sending to printer");
  };

  const createLpn = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!shp) return;
    setBusy(true);
    let sscc: string;
    try {
      const res = await runServerFn(
        () => allocateSsccFn({ data: { tenantId: shp.tenant_id } }),
        "Allocate SSCC",
      );
      sscc = res.sscc;
    } catch {
      setBusy(false);
      return;
    }
    try {
      await runServerFn(
        () =>
          createLpnFn({
            data: {
              shipmentId: shp.id,
              sscc,
              type,
                              weightGrams: weight ? Math.round(Number(weight) * 28.3495) : null,
            },
          }),
        "Create LPN",
      );
    } finally {
      setBusy(false);
    }
    toast.success(`LPN ${sscc} created`);
    setOpen(false); setWeight(""); setType("pallet");
    refetchLpns();
    qc.invalidateQueries({ queryKey: ["lpns"] });
  };

  if (!shp) return <div className="p-4 sm:p-6 text-muted-foreground">Loading…</div>;

  return (
    <div className="p-4 sm:p-6 space-y-4">
      <Card>
        <CardHeader className="flex flex-row items-start justify-between">
          <div>
            <CardTitle className="text-xl">Shipment · {shp.orders?.order_number ?? shp.id.slice(0, 8)}</CardTitle>
            <p className="text-sm text-muted-foreground mt-1">
              {(shp.carrier ?? "—")}{shp.service_level ? ` · ${shp.service_level}` : ""}
              {shp.tracking_number ? ` · ${shp.tracking_number}` : ""}
            </p>
            {summary && (
              <div className="mt-3">
                <ShipmentSummaryHeader summary={summary} />
              </div>
            )}
          </div>
          <div className="flex items-center gap-2">
            <StatusPill tone={statusTone(shp.status)} dot>{shp.status}</StatusPill>
            <Link to="/app/shipments/$shipmentId/bol" params={{ shipmentId }}>
              <Button variant="outline" size="sm"><FileText className="h-4 w-4 mr-2" />Bill of Lading</Button>
            </Link>
            <Link to="/app/shipments/$shipmentId/pallet-labels" params={{ shipmentId }} target="_blank">
              <Button variant="outline" size="sm"><Printer className="h-4 w-4 mr-2" />Pallet labels</Button>
            </Link>
          </div>
        </CardHeader>
      </Card>

      {bolEval?.required && (
        <Card className="border-amber-500/40 bg-amber-500/5">
          <CardContent className="py-3 flex items-start justify-between gap-4">
            <div className="flex items-start gap-2 text-sm">
              <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5" />
              <div>
                <div className="font-medium">
                  Bill of Lading required
                  {activeBol?.bol_number ? (
                    <span className="ml-2 font-mono text-xs text-muted-foreground">{activeBol.bol_number} · {activeBol.status}</span>
                  ) : activeBol ? (
                    <span className="ml-2 text-xs text-muted-foreground">draft in progress</span>
                  ) : null}
                </div>
                <ul className="text-xs text-muted-foreground list-disc list-inside">
                  {bolEval.reasons.map((r, i) => <li key={i}>{r}</li>)}
                </ul>
              </div>
            </div>
            <Link to="/app/shipments/$shipmentId/bol" params={{ shipmentId }}>
              <Button size="sm">{activeBol?.status === "finalized" ? "View BOL" : activeBol ? "Continue draft" : "Create BOL"}</Button>
            </Link>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2"><Truck className="h-4 w-4" />Carrier label</CardTitle>
          <div className="flex items-center gap-2">
            {shp.label_url ? (
              <>
                <a href={shp.label_url} target="_blank" rel="noreferrer">
                  <Button variant="outline" size="sm"><ExternalLink className="h-4 w-4 mr-2" />View label</Button>
                </a>
                <Button variant="outline" size="sm" onClick={onReprintLabel}>
                  <Printer className="h-4 w-4 mr-2" />Reprint
                </Button>
                <Button variant="ghost" size="sm" onClick={onVoidLabel}><X className="h-4 w-4 mr-2" />Void</Button>
              </>
            ) : (
              <Dialog open={labelOpen} onOpenChange={setLabelOpen}>
                <Button size="sm" variant="outline" onClick={openLabelDialog}>
                  <Truck className="h-4 w-4 mr-2" />Buy label
                </Button>
                <DialogContent>
                  <DialogHeader><DialogTitle>Purchase shipping label</DialogTitle></DialogHeader>
                  <form onSubmit={onBuyLabel} className="space-y-4">
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-2">
                        <Label>Carrier</Label>
                        <Select value={labelCarrier} onValueChange={(v) => { setLabelCarrier(v as typeof labelCarrier); setLabelService(services[v][0].value); }}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="ups">UPS</SelectItem>
                            <SelectItem value="usps">USPS</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Service</Label>
                        <Select value={labelService} onValueChange={setLabelService}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {services[labelCarrier].map((s) => (
                              <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="grid grid-cols-4 gap-3">
                      <div className="space-y-2 col-span-1">
                        <Label>Weight (oz)</Label>
                        <Input type="number" step="0.01" min="0.1" value={labelWeight} onChange={(e) => setLabelWeight(e.target.value)} />
                      </div>
                      <div className="space-y-2 col-span-1">
                        <Label>L (in)</Label>
                        <Input type="number" step="0.1" min="0.1" value={labelLength} onChange={(e) => setLabelLength(e.target.value)} />
                      </div>
                      <div className="space-y-2 col-span-1">
                        <Label>W (in)</Label>
                        <Input type="number" step="0.1" min="0.1" value={labelWidth} onChange={(e) => setLabelWidth(e.target.value)} />
                      </div>
                      <div className="space-y-2 col-span-1">
                        <Label>H (in)</Label>
                        <Input type="number" step="0.1" min="0.1" value={labelHeight} onChange={(e) => setLabelHeight(e.target.value)} />
                      </div>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Prefilled from the packed carton — edit before purchase if needed.
                      Uses the active UPS/USPS integration; address warnings are accepted automatically.
                    </p>
                    <DialogFooter>
                      <Button type="submit" disabled={labelBusy}>
                        {labelBusy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Buy label
                      </Button>
                    </DialogFooter>
                  </form>
                </DialogContent>
              </Dialog>
            )}
          </div>
        </CardHeader>
        {shp.label_url && (
          <CardContent>
            <div className="text-sm">
              <div className="font-mono select-all">{shp.tracking_number}</div>
              <div className="text-muted-foreground text-xs capitalize">
                {shp.label_provider} · {shp.service_level}
                {typeof shp.rate_cents === "number" && shp.rate_cents > 0 ? ` · $${(shp.rate_cents / 100).toFixed(2)}` : ""}
              </div>
            </div>
          </CardContent>
        )}
      </Card>

      {compliance && (compliance.policy.source !== "default" || compliance.blockingReasons.length > 0) && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="text-base">Retail Compliance</CardTitle>
              <p className="text-xs text-muted-foreground mt-1">
                Policy source: {compliance.policy.source}
                {compliance.policy.routingGuideId ? ` · routing guide ${compliance.policy.routingGuideId.slice(0, 8)}` : ""}
              </p>
            </div>
            <Badge variant={compliance.cleared ? "secondary" : "destructive"}>
              {compliance.cleared ? "Clear to ship" : "Blocked"}
            </Badge>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-3 gap-3 text-sm">
              <ComplianceArtifactRow
                label="SSCC labels"
                status={compliance.artifacts.sscc}
                detail={`${compliance.cartons.sscc_assigned}/${compliance.cartons.total} cartons`}
              />
              <ComplianceArtifactRow
                label="856 ASN"
                status={compliance.artifacts.asn856}
                detail={compliance.envelope.state
                  ? `${compliance.envelope.state}${compliance.envelope.control_number ? ` · #${compliance.envelope.control_number}` : ""}`
                  : "not queued"}
              />
              <ComplianceArtifactRow
                label="Bill of Lading"
                status={compliance.artifacts.bol}
                detail={compliance.bol.bol_number ? `${compliance.bol.bol_number} · ${compliance.bol.status}` : compliance.bol.status ?? "—"}
              />
            </div>
            {compliance.blockingReasons.length > 0 && (
              <div className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-xs">
                <div className="font-medium text-destructive mb-1">Blocking reasons</div>
                <ul className="list-disc list-inside space-y-0.5 text-muted-foreground">
                  {compliance.blockingReasons.map((r, i) => <li key={i}>{r}</li>)}
                </ul>
              </div>
            )}
            {compliance.warnings.length > 0 && (
              <div className="rounded-md border border-amber-500/40 bg-amber-500/5 p-3 text-xs">
                <div className="font-medium text-amber-700 mb-1">Warnings</div>
                <ul className="list-disc list-inside space-y-0.5 text-muted-foreground">
                  {compliance.warnings.map((r, i) => <li key={i}>{r}</li>)}
                </ul>
              </div>
            )}
            {compliance.artifacts.asn856 !== "not_required" && (
              <Dialog open={resendOpen} onOpenChange={setResendOpen}>
                <DialogTrigger asChild>
                  <Button size="sm" variant="outline">Resend 856 (admin)</Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader><DialogTitle>Resend 856 ASN</DialogTitle></DialogHeader>
                  <p className="text-sm text-muted-foreground">
                    Admin only. A new envelope is created with a fresh per-partner control number.
                    The previous envelope is preserved for audit.
                  </p>
                  <div className="space-y-2">
                    <Label>Reason</Label>
                    <Input value={resendReason} onChange={(e) => setResendReason(e.target.value)} placeholder="e.g. partner asked to retransmit" />
                  </div>
                  <DialogFooter>
                    <Button variant="ghost" onClick={() => setResendOpen(false)} disabled={resendBusy}>Cancel</Button>
                    <Button onClick={onResend856} disabled={resendBusy}>
                      {resendBusy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                      Resend
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            )}
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2"><Layers className="h-4 w-4" />License Plates (LPN/SSCC)</CardTitle>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button size="sm" variant="outline"><Plus className="h-4 w-4 mr-2" />New LPN</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Create LPN</DialogTitle></DialogHeader>
              <form onSubmit={createLpn} className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2"><Label>Type</Label>
                    <Select value={type} onValueChange={(v) => setType(v as typeof type)}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="pallet">Pallet</SelectItem>
                        <SelectItem value="carton">Carton</SelectItem>
                        <SelectItem value="tote">Tote</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2"><Label>Weight (oz)</Label><Input type="number" step="0.01" value={weight} onChange={(e) => setWeight(e.target.value)} placeholder="optional" /></div>
                </div>
                <p className="text-xs text-muted-foreground">SSCC-18 is generated from the tenant's GS1 prefix with mod-10 check digit.</p>
                <DialogFooter><Button type="submit" disabled={busy}>{busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Create</Button></DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </CardHeader>
        <CardContent>
          {lpns.length === 0 ? (
            <p className="text-sm text-muted-foreground py-6 text-center">No LPNs yet. Create one to print pallet/carton labels.</p>
          ) : (
            <div className="space-y-3">
              {lpns.map((l) => (
                <div key={l.id} className="border rounded-md p-4 flex items-center justify-between gap-4">
                  <div className="flex items-center gap-4">
                    <div className="bg-white p-2 rounded border">
                      <Barcode value={l.sscc} format="CODE128" height={50} />
                    </div>
                    <div>
                      <div className="font-mono text-xs">{l.sscc}</div>
                      <div className="text-xs text-muted-foreground capitalize">{l.type} · {l.status}{l.weight_grams ? ` · ${(l.weight_grams / 28.3495).toFixed(2)} oz` : ""}</div>
                    </div>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => window.print()}>
                    <Printer className="h-4 w-4 mr-2" />Print
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2"><Truck className="h-4 w-4" />Tracking events</CardTitle>
        </CardHeader>
        <CardContent>
          {events.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4 text-center">
              No tracking events yet. Carrier webhooks post here in real time.
            </p>
          ) : (
            <div className="space-y-2">
              {events.map((e) => (
                <div key={e.id} className="flex items-start justify-between border-l-2 border-primary/40 pl-3 py-1">
                  <div>
                    <div className="text-sm font-medium capitalize">{e.status.replace(/_/g, " ")}</div>
                    {e.description && <div className="text-xs text-muted-foreground">{e.description}</div>}
                    {e.location && <div className="text-xs text-muted-foreground">{e.location}</div>}
                  </div>
                  <div className="text-xs text-muted-foreground tabular-nums whitespace-nowrap">
                    {new Date(e.event_at).toLocaleString()}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Button variant="ghost" size="sm" onClick={() => navigate({ to: "/app/shipments" })}>← Back to shipments</Button>
    </div>
  );
}